import { supabaseFetch } from "./supabaseClient";

const DEFAULT_LOCKOUT_TIMEOUT_MS = 4000;

/**
 * Returns true only when Supabase clearly reports the user has activated V2
 * (and is therefore locked out of V1). Fail-open by design: any error, a
 * missing RPC, an unexpected response shape, OR a slow/hung request (bounded
 * by timeoutMs) returns false. This guarantees a Supabase problem can never
 * lock the whole team out of V1 AND can never freeze the V1 auth path, which
 * awaits this check before letting a user in. The feature stays dormant until
 * the get_v1_lockout_status RPC is deployed (a 404 fails open).
 */
export async function isLockedOutOfV1(
  firebaseUid,
  { timeoutMs = DEFAULT_LOCKOUT_TIMEOUT_MS } = {},
) {
  if (!firebaseUid) return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const result = await supabaseFetch("/rpc/get_v1_lockout_status", {
      method: "POST",
      body: JSON.stringify({ p_firebase_uid: firebaseUid }),
      signal: controller.signal,
    });
    return result === true;
  } catch (err) {
    console.warn("V1 lockout check failed open:", err?.message ?? err);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Shared enforcement used by BOTH the V1 auth-state listener (login / session
 * resume) and the tab-focus re-check. superAdmins are exempt and never hit
 * Supabase. When an activated non-admin is detected it signs them out of V1
 * and reports locked=true so the caller can render the takeover screen.
 * Fail-open behavior is inherited from isLockedOutOfV1.
 *
 * @param {{uid: string, isSuperAdmin: boolean}|null} user resolved Firebase user
 * @param {{signOut?: () => Promise<void>}} deps
 * @returns {Promise<boolean>} true if the user is locked out (and signed out)
 */
export async function enforceV1Lockout(user, { signOut } = {}) {
  if (!user || user.isSuperAdmin) return false;
  const locked = await isLockedOutOfV1(user.uid);
  if (locked && typeof signOut === "function") {
    await signOut();
  }
  return locked;
}
